import React, { useState } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';

import useFormContext from 'dpl/shared/form/hooks/useFormContext';
import ClickableDiv from 'dpl/common/components/ClickableDiv';
import { PhotographPropTypes } from 'dpl/shared/utils/customPropTypes';
import CardMediaCarousel from 'dpl/breeder_search_results/components/CardMediaCarousel';
import SmartImage from 'dpl/components/SmartImage';
import Badge from 'dpl/common/design_system/Badge';
import { isLessThanBreakpoint } from 'dpl/util/grid';

const IS_LT_MD = isLessThanBreakpoint('md');
const NO_PREFERENCE_VALUE = '';
const IMAGE_SIZE = IS_LT_MD ? 'calc(100vw - 40px)' : '235px';
const DEFAULT_OPTIONS_VISIBLE = 6;

function PuppySelectorQuestionOption({
  isOptionSelected,
  setSelectedPuppy,
  option
}) {
  return (
    <ClickableDiv
      className={classnames(
        'PuppySelectorQuestionOption h-100 w-100 hover-b--midnight db bg-white br12 box-shadow-hover',
        { 'PuppySelectorQuestionOption--selected': isOptionSelected(option) }
      )}
      onClick={() => setSelectedPuppy(option.value)}
    >
      <div className="aspect-ratio aspect-ratio--1x1 z-0">
        <div className="aspect-ratio--object">
          <div className="h-100 w-100 f0">
            {option.all_photographs.length > 0 ? (
              <CardMediaCarousel
                mediaFiles={option.all_photographs}
                smartImageProps={{
                  Tag: 'div',
                  className: 'w-100 h-100 bg-center cover',
                  loadingClass: 'bg-gray-300',
                  allowContextMenu: true,
                  height: IMAGE_SIZE,
                  width: IMAGE_SIZE,
                  crop: true
                }}
                lazyBuffer={window.outerHeight * 2}
                isPuppyCard
              />
            ) : (
              <SmartImage
                Tag="div"
                className="w-100 h-100 bg-center cover"
                src={option.profile_photo_url}
                lazy
                crop
              />
            )}
          </div>
        </div>
      </div>
      <div className="pv4 ph4 pv3-md ph3-md">
        <div className="flex items-center justify-between">
          <h6 className="lh-title truncate w-60 w-50-md font-24 md:font-20 lg:font-20">
            {option.label}
          </h6>
          <Badge size="xsmall" color="teal">
            {option.ready_for_adoption_date_v2}
          </Badge>
        </div>
        <p className="font-14 secondary mv1">{option.subtext}</p>
      </div>
    </ClickableDiv>
  );
}

PuppySelectorQuestionOption.propTypes = {
  option: PropTypes.shape({
    value: PropTypes.string,
    label: PropTypes.string,
    available: PropTypes.bool,
    subtext: PropTypes.string,
    profile_photo_url: PropTypes.string,
    ready_for_adoption_date_v2: PropTypes.string,
    all_photographs: PropTypes.arrayOf(
      PropTypes.shape({
        ...PhotographPropTypes,
        url: PropTypes.string,
        urls: PropTypes.arrayOf(PropTypes.string)
      })
    )
  }).isRequired,
  setSelectedPuppy: PropTypes.func.isRequired,
  isOptionSelected: PropTypes.func.isRequired
};

export default function PuppySelectorQuestion({ name, options }) {
  const { setValue, formFieldValue } = useFormContext(name);
  const [displayHiddenOptions, setDisplayHiddenOptions] = useState(false);
  const hiddenOptionsAvailable = options.length > DEFAULT_OPTIONS_VISIBLE;

  function isOptionSelected(opt) {
    return opt.value === formFieldValue;
  }

  function setSelectedPuppy(dogId) {
    setValue(name, dogId);
  }

  function onShowMoreClick() {
    setDisplayHiddenOptions(true);
  }

  return (
    <div className="PuppySelectorQuestion QuestionLayout">
      <ul className="PuppySelectorQuestion__container flex flex-wrap justify-center tl mb6 mb8-md">
        {options.slice(0, DEFAULT_OPTIONS_VISIBLE - 1).map(option => (
          <li
            key={option.value}
            data-test-id={option.value}
            className="PuppySelectorQuestion__option w-100 w-auto-m mb6 mb4-md mh2-md"
          >
            <PuppySelectorQuestionOption
              option={option}
              isOptionSelected={isOptionSelected}
              setSelectedPuppy={setSelectedPuppy}
            />
          </li>
        ))}
        {displayHiddenOptions &&
          hiddenOptionsAvailable &&
          options.slice(DEFAULT_OPTIONS_VISIBLE, options.length).map(option => (
            <li
              key={option.value}
              data-test-id={option.value}
              className="PuppySelectorQuestion__option w-100 w-auto-m"
            >
              <PuppySelectorQuestionOption
                option={option}
                isOptionSelected={isOptionSelected}
                setSelectedPuppy={setSelectedPuppy}
              />
            </li>
          ))}
      </ul>
      {!displayHiddenOptions && hiddenOptionsAvailable && (
        <div className="tc">
          <button
            type="button"
            className="underline fw-medium stone-700 font-14 mb6"
            onClick={() => onShowMoreClick()}
          >
            Show more
          </button>
        </div>
      )}
      <div className="tc">
        <button
          className={classnames(
            'font-16 ba hover-b--midnight br12 secondary ph4 pv3 bg-white',
            isOptionSelected({ value: NO_PREFERENCE_VALUE })
              ? 'b--stone-700 stone-700'
              : 'b--default'
          )}
          type="button"
          onClick={() => setSelectedPuppy(NO_PREFERENCE_VALUE)}
        >
          I don’t have a puppy preference
        </button>
      </div>
    </div>
  );
}

PuppySelectorQuestion.propTypes = {
  name: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
      available: PropTypes.bool,
      subtext: PropTypes.string,
      profile_photo_url: PropTypes.string,
      all_photographs: PropTypes.arrayOf(
        PropTypes.shape({
          ...PhotographPropTypes,
          url: PropTypes.string,
          urls: PropTypes.arrayOf(PropTypes.string)
        })
      )
    })
  ).isRequired
};
